// Graphic 1 of 4 — pass rate by week, deploys flagged, incident weeks banded.
// One series, one question: "is the job getting better or worse, and did a
// release or an incident move it?" Deploy carets reuse the run-chart idiom;
// the red band is the board's incident window ⟨series[].incident_clusters > 0⟩.
import { useState } from 'react';
import { useChart, tooltipStyle, axisStyle } from '../../lib/useChart';
import { chrome, seriesOne, classificationColor } from '../../lib/palette';
import { pct, esc } from '../../lib/format';
import { weekLabel, incidentBands, deployMarks } from '../../lib/trends';
import ChartCard from '../ChartCard';

export default function WeeklyPassRate({ series, mode }) {
  const [showTable, setShowTable] = useState(false);
  const c = chrome(mode);
  const color = seriesOne(mode);
  const incidentRed = classificationColor('product', mode);
  const bands = incidentBands(series);
  const deploys = deployMarks(series);

  const [ref] = useChart(
    () => ({
      backgroundColor: 'transparent',
      grid: { left: 44, right: 40, top: 34, bottom: 28 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'line', lineStyle: { color: c.axis, width: 1 } },
        ...tooltipStyle(c),
        formatter: (params) => {
          const w = series[params[0].dataIndex];
          const deploysLine = w.deploy_versions.length
            ? `<div style="color:${c.textSecondary};margin-top:4px">deploys: ${esc(w.deploy_versions.join(', '))}</div>` : '';
          const incidentLine = w.incident_clusters > 0
            ? `<div style="color:${incidentRed}">${esc(w.incident_clusters)} backend incident${w.incident_clusters === 1 ? '' : 's'} first seen</div>` : '';
          return (
            `<div style="font-weight:600;font-size:14px">${esc(pct(w.pass_rate))}</div>` +
            `<div style="color:${c.textSecondary}">week of ${esc(weekLabel(w.week_start))} · ${esc(w.runs)} run${w.runs === 1 ? '' : 's'} · ${esc(w.failed)} failed of ${esc(w.executed)}</div>` +
            incidentLine + deploysLine
          );
        },
      },
      xAxis: {
        type: 'category',
        data: series.map((w) => weekLabel(w.week_start)),
        boundaryGap: false,
        ...axisStyle(c),
        axisLabel: { color: c.muted, fontSize: 10 },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        scale: true,
        max: (v) => Math.min(100, Math.ceil(v.max + 1)),
        ...axisStyle(c),
        axisLine: { show: false },
        axisLabel: { color: c.muted, fontSize: 11, formatter: (v) => `${v}%` },
      },
      series: [
        {
          type: 'line',
          data: series.map((w) => w.pass_rate),
          // A quiet week is a real gap, not an interpolated guess.
          connectNulls: false,
          smooth: false,
          lineStyle: { width: 2, color, cap: 'round', join: 'round' },
          itemStyle: { color, borderColor: c.surface, borderWidth: 2 },
          symbol: 'circle',
          symbolSize: 7,
          label: {
            show: true,
            position: 'right',
            color: c.textPrimary,
            fontSize: 12,
            fontWeight: 600,
            formatter: (p) => (p.dataIndex === series.length - 1 && p.value != null ? pct(p.value) : ''),
          },
          // Deploy flags — same caret idiom as the per-run chart (MCIP-24).
          markLine: {
            symbol: ['none', 'triangle'],
            symbolSize: 7,
            symbolRotate: 180,
            silent: true,
            label: { show: true, position: 'end', formatter: 'deploy', color: c.muted, fontSize: 10, padding: [0, 0, 4, 0] },
            lineStyle: { color: c.axis, width: 1, type: 'solid' },
            data: deploys.map((d) => ({ xAxis: d.i })),
          },
          // Incident windows — a light red band under the line, never over it.
          markArea: {
            silent: true,
            itemStyle: { color: incidentRed, opacity: 0.08 },
            data: bands.map(([a, b]) => [{ xAxis: a }, { xAxis: b }]),
          },
        },
      ],
    }),
    [series],
    { mode }
  );

  return (
    <ChartCard
      title="Pass rate by week"
      subtitle="carets = deploy weeks · red band = weeks with backend incidents"
      showTable={showTable}
      onToggleTable={() => setShowTable((v) => !v)}
      table={
        <table className="data-table">
          <thead>
            <tr><th>Week of</th><th className="num">Pass rate</th><th className="num">Runs</th><th className="num">Failed</th><th className="num">Incidents</th><th>Deploys</th></tr>
          </thead>
          <tbody>
            {series.map((w) => (
              <tr key={w.week_start}>
                <td>{weekLabel(w.week_start)}</td>
                <td className="num">{pct(w.pass_rate)}</td>
                <td className="num">{w.runs}</td>
                <td className="num">{w.failed}</td>
                <td className="num">{w.incident_clusters}</td>
                <td>{w.deploy_versions.join(', ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    >
      <div ref={ref} className="chart chart--trend" />
    </ChartCard>
  );
}
